import React, { useState } from "react";
import AddModal from "../modal/AddModal";
import { InputComponent } from "../../../core/Forms";
import LocationSelector from "../../../common/Address";
import PhoneInputComponent from "../../../common/PhoneInputComponent";
import {
  clearFieldError,
  formatNumber,
  validatePhoneNumber,
} from "../../../../utils";
import { useTranslation } from "react-i18next";
import { Button } from "../../../common";
import { CustomerT } from "../../../../types";
import { ICustomer } from "../../../../@types";
import { createCustomer } from "../../../../backend-services";
import { validateCustomerForm } from "../../../../modules/customers/customers";
import { showToast } from "../../../../utils/toastUtils";
interface AddNewCustomerProps {
  setIsOpen: (isOpen: boolean) => void;
  setFormData: React.Dispatch<React.SetStateAction<ICustomer>>;
  fetchCustomers?: () => void;
  handleSetCustomer: (newCustomer: ICustomer) => void;
  onClose?: () => void;
}

const AddNewCustomerDesktop: React.FC<AddNewCustomerProps> = ({
  setIsOpen,
  fetchCustomers,
  setFormData,
  handleSetCustomer,
  onClose,
}) => {
  const { t } = useTranslation("customer");
  const [customer, setCustomer] = React.useState<CustomerT>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    state: "",
    city: "",
    businessName: "",
  });
  const [showAddressFields, setShowAddressFields] = useState(false);
  const [customerType, setCustomerType] = useState("individual");

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShowAddressFields(e.target.checked);
  };
  const [phone, setPhone] = React.useState("");
  const [state, setState] = React.useState("");
  const [city, setCity] = React.useState("");
  const [firstName, setFirstName] = React.useState(customer?.firstName);
  const [lastName, setlastName] = React.useState(customer?.lastName);
  const [email, setEmail] = React.useState(customer?.email);
  const [address, setAddress] = React.useState(customer?.address);
  const [isLoading, setIsLoading] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");
  const [backendErrorMessage, setBackendErrorMessage] = React.useState("");
  const [businessName, setBusinessName] = React.useState(
    customer?.businessName,
  );
  const [validationResults, setValidationResults] = React.useState({
    firstName: "",
    lastName: "",
    phone: "",
    businessName: "",
  });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    setCustomer((prevCustomer) => ({
      ...prevCustomer,
      [name]: value,
    }));

    // Update individual states if needed
    switch (name) {
      case "firstName":
        setFirstName(value);
        break;
      case "lastName":
        setlastName(value);
        break;
      case "email":
        setEmail(value);
        break;
      case "address":
        setAddress(value);
        break;
      case "businessName":
        setBusinessName(value);
        break;
      default:
        break;
    }
    clearFieldError(name, validationResults, setValidationResults);
  };

  const handlePhoneNumberChange = (value: string) => {
    const normalizedValue = value.replace(/\D/g, "");
    const isValidPhoneNumber = validatePhoneNumber(normalizedValue);

    if (isValidPhoneNumber) {
      const formattedPhoneNumber = normalizedValue.startsWith("+")
        ? normalizedValue
        : `+${normalizedValue}`;

      setPhone(formattedPhoneNumber);
      setValidationResults({ ...validationResults, phone: "" });
    } else {
      setValidationResults({
        ...validationResults,
        phone: "Invalid phone format",
      });
    }
  };
  const clearPhoneError = () => {
    setValidationResults({ ...validationResults, phone: "" });
  };
  const handleLocationChange = (
    selectedState: string,
    cities: string | any[],
  ) => {
    setState(selectedState);
    setCity(cities.length > 0 ? cities[0] : "");

    setFormData((prevData: any) => ({
      ...prevData,
      state: selectedState,
      city: cities.length > 0 ? cities[0] : "",
    }));
  };

  function isErrorResponse(
    error: any,
  ): error is { response: { data: { message: string } } } {
    return (
      typeof error === "object" &&
      error.response &&
      typeof error.response === "object" &&
      error.response.data &&
      typeof error.response.data === "object" &&
      error.response.data.message
    );
  }

  const handleCreateCustomer = async () => {
    setIsLoading(true);
    const { hasErrors, errors } = validateCustomerForm({
      firstName,
      lastName,
      phone,
      email,
      businessName,
      customerType,
    });
    setValidationResults(errors);

    if (hasErrors) {
      setIsLoading(false);
      return;
    }

    try {
      const customerData = await createCustomer({
        ...customer,
        phone,
        isOptedIn: false,
        outletId: "",
      });

      setIsLoading(false);
      handleSetCustomer(customerData);
      // Check for duplicate customer
      if (
        customerData &&
        !customerData.firstName &&
        !customerData.lastName &&
        !customerData.metadata
      ) {
        showToast(
          "A customer with this phone number or email already exists.",
          "error",
        );
      } else {
        fetchCustomers && fetchCustomers();
        onClose?.();
        setIsOpen(false);
        showToast("Customer created successfully!", "success");
      }
    } catch (error: any) {
      setIsLoading(false);
      showToast(
        error.message || "An error occurred while creating the customer.",
        "error",
      );
    }
    onClose?.();
  };

  return (
    <>
      {/* Desktop */}
      <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black bg-opacity-50">
        {/* Modal content */}
        <div
          className={`w-full max-w-2xl p-5 bg-white rounded-t-[12px] lg:rounded-[12px] max-h-[90vh] no-scrollbar shadow-lg overflow-y-auto flex flex-col grow`}
        >
          <div className="hidden md:block ">
            <div className=" md:h-full w-full pt-12 md:pt-1 flex flex-col justify-between">
              <div>
                <div className="mb-5 pb-2">
                  <div className="w-full flex flex-col justify-center ">
                    <p className="font-semibold text-[20px] mb-3 leading-6 text-[#000000]">
                      Add Customer
                    </p>
                    <p className="text-[#475467] font-medium">
                      Select customer type
                    </p>
                  </div>
                </div>

                <div className="bg-[#F2F4F7] p-1 mb-5 flex items-center rounded-lg w-max">
                  <button
                    onClick={() => {
                      setCustomerType("individual");
                    }}
                    className={`py-2 px-3 ${customerType == "individual" ? "bg-white rounded-lg text-semibold text-[#1D2939] " : "bg-transparent text-[#475467]"}  `}
                  >
                    Individual
                  </button>
                  <button
                    onClick={() => {
                      setCustomerType("business");
                    }}
                    className={`py-2 px-3 ${customerType == "business" ? "bg-white rounded-lg text-semibold text-[#1D2939] " : "bg-transparent text-[#475467]"}  `}
                  >
                    Business
                  </button>
                </div>
                {customerType == "individual" ? (
                  <div className="flex justify-between mb-3 gap-3">
                    <div className="w-full">
                      <label
                        htmlFor="First Name"
                        className="font-medium text-[14px] leading-5 text-[#000000]"
                      >
                        First name
                        <span className="text-red-500 ml-1">*</span>
                      </label>
                      <InputComponent
                        placeholder="First Name"
                        name="firstName"
                        type="text"
                        error={validationResults.firstName}
                        value={firstName}
                        handleChange={handleInputChange}
                        className="w-full placeholder:text-gray-400 border border-gray-300"
                      />
                    </div>

                    <div className="w-full">
                      <label
                        htmlFor="Last Name"
                        className="font-medium text-[14px] leading-5 text-[#000000]"
                      >
                        Last name
                        <span className="text-red-500 ml-1">*</span>
                      </label>
                      <InputComponent
                        placeholder="Last Name"
                        name="lastName"
                        type="text"
                        value={lastName}
                        handleChange={handleInputChange}
                        className="w-full placeholder:text-gray-400 border border-gray-300"
                        error={validationResults.firstName}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-between mb-3 gap-3">
                    <div className="w-full">
                      <label
                        htmlFor="BusinessName"
                        className="font-medium text-[14px] leading-5 text-[#000000]"
                      >
                        {t("customer.businessName")}
                        <span className="text-red-500 ml-1">*</span>
                      </label>
                      <InputComponent
                        name="businessName"
                        placeholder="Business Name"
                        type="text"
                        value={businessName}
                        handleChange={handleInputChange}
                        className="w-full placeholder:text-gray-400 border border-gray-300"
                        error={validationResults.businessName}
                      />
                    </div>
                  </div>
                )}
                <div className="flex justify-between gap-3">
                  <div className="mt-3 w-full">
                    <label
                      htmlFor="Phone Number"
                      className="font-medium text-[14px] leading-5 text-[#000000]"
                    >
                      {t("customer.phone")}
                    </label>
                    <PhoneInputComponent
                      onPhoneNumberChange={handlePhoneNumberChange}
                      initialPhoneNumber={phone}
                      inputClass="onboarding"
                      containerClass="onboarding"
                      clearPhoneError={clearPhoneError}
                    />
                    {validationResults.phone &&
                      validationResults.phone.length > 0 && (
                        <p className="text-red-500 text-sm">
                          {validationResults.phone}
                        </p>
                      )}
                  </div>
                  <div className=" mt-3 w-full">
                    <label
                      htmlFor="Email"
                      className="font-medium text-[14px] leading-5 text-[#000000]"
                    >
                      Email
                    </label>
                    <InputComponent
                      name="email"
                      placeholder="Email Address"
                      type="text"
                      value={email}
                      handleChange={handleInputChange}
                      className="w-full placeholder:text-gray-400 border border-gray-300"
                    />
                  </div>
                </div>
                <div className="flex items-center mt-[2em] biglg:ml-[1em] xl:ml-[0.3em]">
                  <label
                    onClick={() => setShowAddressFields(!showAddressFields)}
                    htmlFor="link-checkbox"
                    className="mr-2 text-sm cursor-pointer font-medium flex items-center text-gray-900 dark:text-gray-300"
                  >
                    {showAddressFields
                      ? "Show less information"
                      : "Show more information"}{" "}
                    <svg
                      width={20}
                      height={20}
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className={showAddressFields ? "rotate-90" : ""}
                    >
                      <path
                        d="M7.5 5l4.293 4.293c.333.333.5.5.5.707 0 .207-.167.374-.5.707L7.5 15"
                        stroke="#344054"
                        strokeWidth={1.5}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </label>
                </div>

                {showAddressFields && (
                  <>
                    <div className="w-full my-3 ">
                      <LocationSelector
                        display="flex flex-row items-center w-full gap-3 "
                        onLocationChange={handleLocationChange}
                        onError={(error: any) => {}}
                        selectedState={state}
                        selectedCity={city}
                        required={false}
                      />
                    </div>
                  </>
                )}
              </div>

              <div className="w-full py-5">
                <div className="mt-[32px] flex gap-6 w-full">
                  <Button
                    outline={true}
                    variant="secondary"
                    disabled={false}
                    text={"Cancel"}
                    size="full"
                    onClick={onClose}
                  />
                  <Button
                    type="button"
                    onClick={() => handleCreateCustomer()}
                    disabled={isLoading}
                    variant="secondary"
                    // className="w-full"
                  >
                    {isLoading ? "Saving..." : "Save"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddNewCustomerDesktop;
